import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import styles from "../Ui.module.css";

/**
 * A "More" button that shows a short list of less frequent actions, so the toolbar fits on a
 * 1366 pixel screen. A disclosure, not an ARIA menu: Tab moves through the actions, Escape closes
 * the list and returns focus to the button, and choosing an action or clicking elsewhere closes it.
 */
export function MoreMenu({
  label = "More",
  children,
}: {
  label?: string;
  /** The actions: buttons or links. Each closes the list when chosen. */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    // Move focus to the first action, so keyboard users land in the list.
    list.current?.querySelector<HTMLElement>("a, button")?.focus();
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  return (
    <div
      ref={root}
      className={styles.more}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
          button.current?.focus();
        }
      }}
    >
      <button
        ref={button}
        type="button"
        className={styles.btn}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {label} <span aria-hidden="true">▾</span>
      </button>
      <div
        ref={list}
        id={id}
        className={styles.moreList}
        hidden={!open}
        onClick={(e) => {
          // Choosing an action closes the list.
          if ((e.target as HTMLElement).closest("a, button")) setOpen(false);
        }}
      >
        {children}
      </div>
    </div>
  );
}
